// =============================================================================
// siteMapSymbols — Coretel's site-map vocabulary (mig 390, 26 Sep 2026)
// =============================================================================
// The things a rigger, a PE or a crane crew needs to see on a mud map, named
// the way we say them. Ours first (the 16 Sep mud-map card: tower/pole,
// shelters and ground equipment, emergency assembly area, drop zone), plus
// the two of HVAC's worth having (spill kit, numbered hazard), plus the crane
// and lift-path symbols the lift plan needs (James, 26 Sep: "a sat image was
// exactly what was missing from ours").
//
// Every feature on a map carries properties.kind = one of these keys. The
// legend, the PDF (piece 2) and the editor palette all read this one list, so
// adding a symbol is one line here and nowhere else.
//
// 8 Oct 2026 — the inner zone draws itself (Ops brief 6, gap 7). James after
// Mount Tamborine: the set-out put pre-assembly about 10 m from the tower and
// that is what put a ground worker in the zone. A tower pin now carries the
// structure type and the highest working point; the inner zone is worked out
// from those (tower half, pole or mast one third, never more than 20 m) and
// drawn as a ring. Parking, the assembly point, laydown and the new
// pre-assembly area are flagged when they sit inside it. A warning, not a
// refusal — "where possible" is the rule.
// =============================================================================

export type SymbolShape = "pin" | "area" | "line";

export interface SiteSymbol {
  kind: string;
  shape: SymbolShape;
  label: string;          // what the legend prints
  short: string;          // what sits on the pin (emoji or 2–4 letters)
  colour: string;         // pin badge / area fill / line stroke
  group: "Site" | "Emergency" | "Lift & plant" | "Zones" | "Routes" | "Other";
  numbered?: boolean;     // hazards, spotters — auto-number as placed
  dashed?: boolean;       // lines
  hatched?: boolean;      // areas
  hint?: string;          // one line under the palette chip
}

export const SITE_SYMBOLS: SiteSymbol[] = [
  // --- the site itself -------------------------------------------------------
  { kind: "tower",     shape: "pin",  label: "Tower / pole",          short: "🗼", colour: "#7C3AED", group: "Site" },
  { kind: "shelter",   shape: "pin",  label: "Shelter / cabinet",     short: "🏠", colour: "#475569", group: "Site" },
  { kind: "gate",      shape: "pin",  label: "Gate / access",         short: "🚧", colour: "#B45309", group: "Site", hint: "Where you get in — code or key in the note" },
  { kind: "parking",   shape: "pin",  label: "Vehicle parking",       short: "🅿", colour: "#64748B", group: "Site" },
  { kind: "laydown",   shape: "area", label: "Laydown / materials",   short: "LAY", colour: "#0891B2", group: "Site" },
  { kind: "preassembly", shape: "area", label: "Pre-assembly area",  short: "PRE", colour: "#0D9488", group: "Site", hint: "Draw it outside the inner zone" },
  { kind: "compound",  shape: "area", label: "Compound / lease area", short: "CMP", colour: "#2563EB", group: "Site" },
  { kind: "access",    shape: "line", label: "Access track",          short: "—",  colour: "#A16207", group: "Routes" },
  { kind: "powerline", shape: "line", label: "Overhead powerlines",   short: "⚡", colour: "#EAB308", group: "Routes", dashed: true, hint: "Keep the exclusion distance in the note" },

  // --- emergency -------------------------------------------------------------
  { kind: "assembly",  shape: "pin",  label: "Emergency assembly point", short: "🚩", colour: "#16A34A", group: "Emergency" },
  { kind: "evac",      shape: "line", label: "Evacuation route",      short: "→",  colour: "#16A34A", group: "Emergency", dashed: true },
  { kind: "first_aid", shape: "pin",  label: "First aid kit",         short: "⛑",  colour: "#DC2626", group: "Emergency" },
  { kind: "fire",      shape: "pin",  label: "Fire extinguisher",     short: "🧯", colour: "#DC2626", group: "Emergency" },
  { kind: "spill",     shape: "pin",  label: "Spill kit",             short: "🛢", colour: "#CA8A04", group: "Emergency" },
  { kind: "hazard",    shape: "pin",  label: "Hazard (numbered)",     short: "⚠",  colour: "#DC2626", group: "Emergency", numbered: true, hint: "Say what it is in the note — it prints in the legend" },

  // --- lift & plant ------------------------------------------------------------
  { kind: "crane",     shape: "pin",  label: "Crane set-up position", short: "🏗", colour: "#EA580C", group: "Lift & plant" },
  { kind: "ewp",       shape: "pin",  label: "EWP position",          short: "EWP", colour: "#2563EB", group: "Lift & plant" },
  { kind: "spotter",   shape: "pin",  label: "Spotter (numbered)",    short: "👷", colour: "#F97316", group: "Lift & plant", numbered: true },
  { kind: "load_path", shape: "line", label: "Load path",             short: "⇢",  colour: "#EA580C", group: "Lift & plant" },
  { kind: "drop_zone", shape: "area", label: "Drop zone",             short: "DZ", colour: "#DC2626", group: "Zones", hatched: true },
  { kind: "exclusion", shape: "area", label: "Exclusion zone",        short: "EX", colour: "#DC2626", group: "Zones" },
  { kind: "work_area", shape: "area", label: "Work area",             short: "WK", colour: "#F59E0B", group: "Zones" },

  // --- anything else -----------------------------------------------------------
  { kind: "pin",       shape: "pin",  label: "Custom pin",            short: "📍", colour: "#0F172A", group: "Other", hint: "Name it — the name is the legend entry" },
  { kind: "area",      shape: "area", label: "Custom area",           short: "▭",  colour: "#0F172A", group: "Other" },
  { kind: "line",      shape: "line", label: "Custom line",           short: "╱",  colour: "#0F172A", group: "Other" },
  { kind: "note",      shape: "pin",  label: "Note",                  short: "✎",  colour: "#0F172A", group: "Other", hint: "A label on the map, nothing more" },
];

export const SYMBOL_BY_KIND: Record<string, SiteSymbol> =
  Object.fromEntries(SITE_SYMBOLS.map(s => [s.kind, s]));

export const SYMBOL_GROUPS = ["Site", "Emergency", "Lift & plant", "Zones", "Routes", "Other"] as const;

export function symbolFor(kind: string | undefined | null): SiteSymbol {
  return (kind && SYMBOL_BY_KIND[kind]) || SYMBOL_BY_KIND.pin;
}

// What a feature carries besides its geometry.
export interface SiteFeatureProps {
  kind: string;
  label?: string;
  number?: number;     // hazards / spotters
  note?: string;
  colour?: string;     // custom pins/areas/lines can override
  structure?: "tower" | "pole";   // tower pin only — pole covers poles and masts
  work_height_m?: number;         // tower pin only — highest working point, metres
}

export type SiteFeature = {
  type: "Feature";
  id: string;
  geometry:
    | { type: "Point"; coordinates: [number, number] }
    | { type: "LineString"; coordinates: [number, number][] }
    | { type: "Polygon"; coordinates: [number, number][][] };
  properties: SiteFeatureProps;
};

// symbol_scale (27 Sep 2026, James: "make the symbol size editable … if we
// zoom out we don't just have these compressed dots") — S 0.75 · M 1 · L 1.4,
// saved with the drawing so the crew's phone and the locked PDF agree.
export type SiteFeatureCollection = { type: "FeatureCollection"; features: SiteFeature[]; symbol_scale?: number };

export const EMPTY_COLLECTION: SiteFeatureCollection = { type: "FeatureCollection", features: [] };

// The legend is whatever is actually on the map, in symbol-list order, with
// custom/numbered items listed by their own label — a legend that lists
// twenty symbols the map does not use is a legend nobody reads.
export function legendFor(fc: SiteFeatureCollection): { symbol: SiteSymbol; label: string; number?: number; note?: string }[] {
  const out: { symbol: SiteSymbol; label: string; number?: number; note?: string }[] = [];
  const seen = new Set<string>();
  const ordered = [...fc.features].sort((a, b) =>
    SITE_SYMBOLS.findIndex(s => s.kind === a.properties.kind) - SITE_SYMBOLS.findIndex(s => s.kind === b.properties.kind)
    || (a.properties.number ?? 0) - (b.properties.number ?? 0));
  for (const f of ordered) {
    const s = symbolFor(f.properties.kind);
    const custom = s.group === "Other" || s.numbered;
    const key = custom ? f.id : s.kind;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      symbol: s,
      label: (custom ? f.properties.label?.trim() : "") || s.label.replace(/\s*\(numbered\)/, ""),
      number: s.numbered ? f.properties.number : undefined,
      note: custom ? f.properties.note : undefined,
    });
  }
  return out;
}

// --- the inner zone (8 Oct 2026) ---------------------------------------------
// Tower: half the highest working point. Pole or mast: one third. Never more
// than 20 m. No height entered means no ring — we do not guess one.
export const INNER_ZONE_MAX_M = 20;
export function innerZoneRadiusM(p: SiteFeatureProps | undefined | null): number | null {
  const h = Number(p?.work_height_m);
  if (!p || p.kind !== "tower" || !Number.isFinite(h) || h <= 0) return null;
  const r = Math.min(INNER_ZONE_MAX_M, h * (p.structure === "pole" ? 1 / 3 : 1 / 2));
  return Math.round(r * 10) / 10;
}

// What should not be inside it: vehicles and the assembly point (the client
// rule), laydown and pre-assembly (ours).
export const KEPT_OUT_OF_INNER_ZONE = ["parking", "assembly", "laydown", "preassembly"];

// Metres from a point to the nearest part of a feature. Flat-earth maths
// around the point — exact enough at 20 m.
function metresTo(from: [number, number], f: SiteFeature): number {
  const kx = 111320 * Math.cos(from[1] * Math.PI / 180), ky = 110540;
  const xy = (c: [number, number]) => [(c[0] - from[0]) * kx, (c[1] - from[1]) * ky] as [number, number];
  if (f.geometry.type === "Point") { const [x, y] = xy(f.geometry.coordinates); return Math.hypot(x, y); }
  const pts = (f.geometry.type === "Polygon" ? f.geometry.coordinates[0] : f.geometry.coordinates).map(xy);
  if (f.geometry.type === "Polygon") {
    // standing inside the area counts as zero
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const [xi, yi] = pts[i], [xj, yj] = pts[j];
      if ((yi > 0) !== (yj > 0) && 0 < (xj - xi) * (0 - yi) / (yj - yi) + xi) inside = !inside;
    }
    if (inside) return 0;
  }
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[i + 1];
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
    best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
  }
  if (pts.length === 1) best = Math.hypot(pts[0][0], pts[0][1]);
  return best;
}

export interface InnerZoneIssue { featureId: string; towerId: string; label: string; metres: number; radius: number }
export function innerZoneIssues(fc: SiteFeatureCollection): InnerZoneIssue[] {
  const out: InnerZoneIssue[] = [];
  for (const t of fc.features) {
    const r = innerZoneRadiusM(t.properties);
    if (r == null || t.geometry.type !== "Point") continue;
    for (const f of fc.features) {
      if (!KEPT_OUT_OF_INNER_ZONE.includes(f.properties.kind)) continue;
      const d = metresTo(t.geometry.coordinates, f);
      if (d < r) out.push({ featureId: f.id, towerId: t.id, label: f.properties.label?.trim() || symbolFor(f.properties.kind).label, metres: Math.round(d), radius: r });
    }
  }
  return out;
}
